// UserMenu — 顶栏头像 dropdown
// 点击头像显示菜单: 显示名 / 邮箱 / 切换公司 / 个人信息 / 登出
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import type { CompanyInfo, User } from '../lib/api';
import { Icon } from './Icon';

interface UserMenuProps {
  user: User;
  availableCompanies: CompanyInfo[];
  currentCompanyId: string | null;
  onSwitchCompany: (companyId: string) => void;
  onLogout: () => void;
}

function getInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?';
}

function getColorFromName(name: string): string {
  // 用 displayName 简单 hash 出 8 种柔和背景色
  const palette = ['bg-accent', 'bg-info', 'bg-good', 'bg-warn', 'bg-ink-2', 'bg-bad'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length] ?? 'bg-accent';
}

export function UserMenu({
  user,
  availableCompanies,
  currentCompanyId,
  onSwitchCompany,
  onLogout,
}: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // 点击外部关闭
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const colorClass = getColorFromName(user.displayName);
  const initial = getInitial(user.displayName);
  const currentCompany = availableCompanies.find((c) => c.id === currentCompanyId);
  // v1.28.3: 有 avatarUrl 用真头像, 否则用色块+initials
  const hasAvatar = !!user.avatarUrl;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 hover:bg-surface-2 rounded-md p-1 pr-2 transition-colors"
        aria-label="用户菜单"
        aria-expanded={open}
      >
        {hasAvatar ? (
          <img
            src={user.avatarUrl}
            alt={user.displayName}
            className="w-8 h-8 rounded-full object-cover border border-line"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div
            className={`w-8 h-8 rounded-full ${colorClass} text-white flex items-center justify-center text-sm font-semibold`}
          >
            {initial}
          </div>
        )}
        <span className="hidden sm:block text-sm text-ink-2">{user.displayName}</span>
        <Icon name="chevron-down" size="xs" className="text-ink-3" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-72 bg-surface border border-line rounded-lg shadow-lg z-50 overflow-hidden animate-fade-in"
          role="menu"
        >
          {/* User info header */}
          <div className="px-4 py-3 border-b border-line bg-surface-2">
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-full ${colorClass} text-white flex items-center justify-center text-base font-semibold shrink-0`}
              >
                {initial}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-ink truncate">{user.displayName}</div>
                <div className="text-xs text-ink-3 truncate">{user.email}</div>
              </div>
            </div>
          </div>

          {/* 切换公司 */}
          {availableCompanies.length > 1 && (
            <div className="py-1.5 border-b border-line">
              <div className="px-4 py-1.5 text-[10px] uppercase tracking-wider text-ink-3">
                切换公司
              </div>
              {availableCompanies.map((c) => {
                const isCurrent = c.id === currentCompanyId;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      onSwitchCompany(c.id);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-1.5 text-left text-sm hover:bg-surface-2 transition-colors ${
                      isCurrent ? 'bg-accent-soft' : ''
                    }`}
                  >
                    <span className="text-base">{c.flag}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs truncate">{c.name}</div>
                    </div>
                    {isCurrent && <Icon name="check" size="sm" className="text-accent" />}
                  </button>
                );
              })}
            </div>
          )}

          {currentCompany && availableCompanies.length === 1 && (
            <div className="px-4 py-2 border-b border-line flex items-center gap-2 text-xs text-ink-3">
              <span>{currentCompany.flag}</span>
              <span>{currentCompany.name}</span>
            </div>
          )}

          {/* 行动 */}
          <div className="py-1.5">
            <Link
              to="/settings"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-1.5 text-sm text-ink-2 hover:bg-surface-2 hover:text-ink transition-colors"
              role="menuitem"
            >
              <Icon name="settings" size="sm" className="text-ink-3" />
              <span>个人信息</span>
            </Link>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onLogout();
                navigate({ to: '/login' });
              }}
              className="w-full flex items-center gap-3 px-4 py-1.5 text-left text-sm text-bad hover:bg-bad/5 transition-colors"
              role="menuitem"
            >
              <Icon name="power" size="sm" />
              <span>登出</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
